import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchRoutes } from '../store/slices/routesSlice';
import { logout } from '../store/slices/authSlice';
import { 
  FiArchive, 
  FiPlus, 
  FiUser, 
  FiChevronDown, 
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiCalendar,
  FiSettings,
  FiLogOut
} from 'react-icons/fi';

// Компоненты попапов
import ArchiveModal from '../components/Modals/ArchiveModal';
import CreateRouteModal from '../components/Modals/CreateRouteModal';
import SettingsModal from '../components/Modals/SettingsModal';
import RouteActionsModal from '../components/Modals/RouteActionsModal';
import Calendar from '../components/Calendar/Calendar';

const MainPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { routes } = useAppSelector((state) => state.routes);
  const { user } = useAppSelector((state) => state.auth);
  
  // Состояния для попапов
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showRouteActions, setShowRouteActions] = useState(false);
  
  // Состояния для маршрутов
  const [selectedRoute, setSelectedRoute] = useState<any>(null);
  const [routeScrollPosition, setRouteScrollPosition] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Состояния для основной области
  const [newEventText, setNewEventText] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchRoutes());
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    setShowUserMenu(false);
  };

  const handleRouteSelect = (route: any) => {
    setSelectedRoute(route);
  };

  const scrollRoutes = (direction: 'left' | 'right') => {
    const container = document.getElementById('routes-container');
    if (container) {
      const scrollAmount = 200;
      const newPosition = direction === 'left' 
        ? routeScrollPosition - scrollAmount 
        : routeScrollPosition + scrollAmount;
      
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      setRouteScrollPosition(newPosition);
    }
  };

  const routesArray = Array.isArray(routes) ? routes : [];
  const activeRoutes = routesArray.filter(route => !route.is_archived);
  const archivedRoutes = routesArray.filter(route => route.is_archived);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Верхний бар */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Левая часть - Архив */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowArchiveModal(true)}
              className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
            >
              <FiArchive className="w-5 h-5" />
              <span>Архив</span>
            </button>
            
            {/* Разделительная черта */}
            <div className="w-px h-6 bg-gray-300"></div>
            
            {/* Список маршрутов с прокруткой */}
            <div className="flex items-center space-x-2">
              {routeScrollPosition > 0 && (
                <button
                  onClick={() => scrollRoutes('left')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
              )}
              
              <div 
                id="routes-container"
                className="flex space-x-2 overflow-x-hidden max-w-md"
                style={{ scrollBehavior: 'smooth' }}
              >
                {activeRoutes.map((route) => (
                  <button
                    key={route.id}
                    onClick={() => handleRouteSelect(route)}
                    className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors ${
                      selectedRoute?.id === route.id
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {route.title}
                  </button>
                ))}
              </div>
              
              {routeScrollPosition < (activeRoutes.length * 150 - 300) && (
                <button
                  onClick={() => scrollRoutes('right')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Правая часть - Создать и пользователь */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              <span>Создать</span>
            </button>
            
            {/* Разделительная черта */}
            <div className="w-px h-6 bg-gray-300"></div>
            
            {/* Меню пользователя */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
              >
                <FiUser className="w-5 h-5" />
                <FiChevronDown className="w-4 h-4" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                    {user?.email}
                  </div>
                  <button
                    onClick={() => {
                      setShowSettingsModal(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <FiSettings className="w-4 h-4" />
                    <span>Настройки</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <FiLogOut className="w-4 h-4" />
                    <span>Выход</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Основная область */}
      <div className="p-6">
        {selectedRoute ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Заголовок маршрута */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  План путешествия
                </h1>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  {isExpanded ? (
                    <FiChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
              
              <button
                onClick={() => setShowRouteActions(true)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <FiMoreVertical className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Добавление нового события */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={newEventText}
                  onChange={(e) => setNewEventText(e.target.value)}
                  placeholder="Добавить событие..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={() => setShowCalendar(true)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <FiCalendar className="w-5 h-5 text-gray-500" />
                </button>
                <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  Добавить
                </button>
              </div>
            </div>

            {/* Содержимое маршрута */}
            {isExpanded && (
              <div className="p-6">
                <div className="text-center text-gray-500 py-8">
                  Содержимое маршрута будет здесь
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Выберите маршрут
            </h3>
            <p className="text-gray-600 mb-6">
              Выберите маршрут из списка выше или создайте новый
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Создать маршрут
            </button>
          </div>
        )}
      </div>

      {/* Модальные окна */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        routes={archivedRoutes}
      />
      
      <CreateRouteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
      
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
      
      <RouteActionsModal
        isOpen={showRouteActions}
        onClose={() => setShowRouteActions(false)}
        route={selectedRoute}
      />
      
      <Calendar
        isOpen={showCalendar}
        onClose={() => setShowCalendar(false)}
        onDateSelect={setSelectedDate}
        selectedDate={selectedDate}
      />
    </div>
  );
};

export default MainPage;
